import { useState, useEffect, useMemo } from "react";
import {
  Plus, Trash2, Save, X, Cog, Loader2, ChevronRight, Pencil,
  History, Link as LinkIcon,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import Modal from "../Modal";
import { toast } from "sonner";
import DictTextarea from "../DictTextarea";

/**
 * PerfConfigDrawer — Iter 374 (Performance Phase B + edit + deps)
 *
 * Admin management for one site:
 *   - Add / edit / delete equipment (rename, description, installed_at).
 *   - Add / edit / delete parameters (all fields + cross-param deps).
 *   - Audit-log popover per entity (shows recent edits).
 */
export default function PerfConfigDrawer({ open, onClose, site, onChanged }) {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedEqId, setSelectedEqId] = useState(null);
  const [params, setParams] = useState([]);
  const [addEqName, setAddEqName] = useState("");
  const [editingEqId, setEditingEqId] = useState(null);
  const [historyEntity, setHistoryEntity] = useState(null); // {entity_type,entity_id,name}

  const load = async () => {
    if (!site) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/perf/equipment?site_id=${site.id}`);
      setEquipment(data);
      if (data.length && !selectedEqId) setSelectedEqId(data[0].id);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  const loadParams = async (eqId) => {
    if (!eqId) return;
    try {
      const { data } = await api.get(`/perf/parameters?equipment_id=${eqId}`);
      setParams(data);
    } catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => { if (open) load(); }, [open, site?.id]);
  useEffect(() => { if (selectedEqId) loadParams(selectedEqId); }, [selectedEqId]);

  const addEquipment = async () => {
    const name = addEqName.trim();
    if (!name) return;
    try {
      await api.post("/perf/equipment", { site_id: site.id, name });
      setAddEqName("");
      await load();
      toast.success("Equipment added");
      onChanged?.();
    } catch (e) { setErr(formatErr(e)); }
  };

  const selectedEq = equipment.find((e) => e.id === selectedEqId);

  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Configure · ${site?.name || "Site"}`} maxWidth="max-w-4xl">
      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 min-h-[440px]" data-testid="perf-config-drawer">
        {/* Equipment list */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Equipment</div>
          {loading && <div className="text-xs text-[#647087]">Loading…</div>}
          {equipment.map((eq) => (
            <EquipmentRow
              key={eq.id}
              eq={eq}
              selected={selectedEqId === eq.id}
              editing={editingEqId === eq.id}
              onSelect={() => setSelectedEqId(eq.id)}
              onEditStart={() => setEditingEqId(eq.id)}
              onEditCancel={() => setEditingEqId(null)}
              onHistory={() => setHistoryEntity({ entity_type: "equipment", entity_id: eq.id, name: eq.name })}
              onSaved={async () => { setEditingEqId(null); await load(); onChanged?.(); }}
            />
          ))}
          {!equipment.length && !loading && (
            <div className="text-xs text-[#647087] py-4">No equipment yet.</div>
          )}
          <div className="flex gap-1 pt-2 border-t border-white/5">
            <input
              value={addEqName}
              onChange={(e) => setAddEqName(e.target.value)}
              placeholder="New equipment name"
              className="hv-input flex-1 text-sm"
              data-testid="perf-config-eq-add-input"
            />
            <button
              type="button"
              onClick={addEquipment}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="perf-config-eq-add-btn"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>

        {/* Parameter editor */}
        <div className="space-y-2">
          {selectedEqId ? (
            <ParameterEditor
              eqId={selectedEqId}
              params={params}
              equipmentName={selectedEq?.name || ""}
              onChanged={() => { loadParams(selectedEqId); onChanged?.(); }}
              onHistory={(p) => setHistoryEntity({ entity_type: "parameter", entity_id: p.id, name: p.name })}
            />
          ) : (
            <div className="text-xs text-[#647087]">Select equipment to configure its parameters.</div>
          )}
        </div>
      </div>
      {err && <div className="mt-3 text-sm text-rose-300">{err}</div>}

      {historyEntity && (
        <AuditHistoryModal
          entity={historyEntity}
          onClose={() => setHistoryEntity(null)}
        />
      )}
    </Modal>
  );
}

/**
 * EquipmentRow — collapsed shows name + gear; expanded shows editable
 * name / description / installed_at with Save + Cancel + Delete.
 */
function EquipmentRow({ eq, selected, editing, onSelect, onEditStart, onEditCancel, onHistory, onSaved }) {
  const [name, setName] = useState(eq.name || "");
  const [description, setDescription] = useState(eq.description || "");
  const [installedAt, setInstalledAt] = useState(eq.installed_at ? eq.installed_at.slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (editing) {
      setName(eq.name || "");
      setDescription(eq.description || "");
      setInstalledAt(eq.installed_at ? eq.installed_at.slice(0, 10) : "");
      setErr("");
    }
  }, [editing, eq]);

  const save = async () => {
    if (!name.trim()) return setErr("Name is required.");
    setBusy(true);
    try {
      await api.patch(`/perf/equipment/${eq.id}`, {
        name: name.trim(),
        description: description || null,
        installed_at: installedAt || null,
      });
      toast.success("Equipment updated");
      onSaved?.();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="hv-card p-3 space-y-2 border-[#1fb6c1]/40" data-testid={`perf-config-eq-edit-${eq.id}`}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Edit equipment</div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Equipment name"
          className="hv-input w-full text-sm"
          data-testid={`perf-config-eq-name-${eq.id}`}
        />
        <DictTextarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          rows={2}
          className="hv-input w-full text-sm resize-none"
          data-testid={`perf-config-eq-desc-${eq.id}`}
        />
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.15em] text-[#647087]">Installed at (optional)</span>
          <input
            type="date"
            value={installedAt}
            onChange={(e) => setInstalledAt(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            data-testid={`perf-config-eq-installed-${eq.id}`}
          />
        </label>
        {err && <div className="text-xs text-rose-300">{err}</div>}
        <div className="flex items-center justify-end gap-1">
          <button type="button" onClick={onEditCancel} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid={`perf-config-eq-save-${eq.id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`p-3 rounded-md border transition ${selected ? "border-[#1fb6c1] bg-[#1fb6c1]/10 text-white" : "border-white/10 hover:border-white/20 text-[#a0aabf]"}`}
      data-testid={`perf-config-eq-${eq.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={onSelect} className="flex items-center gap-2 min-w-0 text-left flex-1">
          <Cog className="w-3.5 h-3.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{eq.name}</div>
            {eq.description && <div className="text-[10px] text-[#647087] truncate">{eq.description}</div>}
            {eq.installed_at && <div className="text-[10px] text-[#647087]">Installed {eq.installed_at.slice(0, 10)}</div>}
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onHistory}
            className="p-1 rounded hover:bg-white/5 text-[#647087]"
            title="View audit history"
            data-testid={`perf-config-eq-history-${eq.id}`}
          >
            <History className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onEditStart}
            className="p-1 rounded hover:bg-white/5 text-[#a0aabf]"
            title="Edit equipment"
            data-testid={`perf-config-eq-edit-btn-${eq.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <ChevronRight className="w-4 h-4 opacity-50" />
        </div>
      </div>
    </div>
  );
}

const TARGET_KINDS = [
  { value: "",       label: "No target — capture values only, no pass/fail check" },
  { value: "exact",  label: "Exact value — value must equal target (± warn/alarm %)" },
  { value: "min",    label: "Minimum — value must be at least this number" },
  { value: "max",    label: "Maximum — value must not exceed this number" },
  { value: "range",  label: "Range — value must fall between a min and max" },
  { value: "yes_no", label: "Yes / No — tech picks Yes or No (no pass/fail)" },
];

const DEP_OPS = [
  { value: "lt",         label: "< (less than ref) — value must be strictly below the ref parameter" },
  { value: "lte",        label: "≤ ref — value must be at or below the ref parameter" },
  { value: "gt",         label: "> ref — value must be strictly above the ref parameter" },
  { value: "gte",        label: "≥ ref — value must be at or above the ref parameter" },
  { value: "eq",         label: "= ref (± tolerance) — value must match the ref parameter (with tolerance)" },
  { value: "max_down",   label: "max downward variance — value may not drop more than N below the ref" },
  { value: "max_up",     label: "max upward variance — value may not rise more than N above the ref" },
  { value: "max_diff",   label: "|value − ref| ≤ threshold — capped drift in BOTH directions" },
  { value: "min_diff",   label: "|value − ref| ≥ threshold — value must differ from the ref by at least N" },
  { value: "max_ratio",  label: "value ≤ ref × ratio — value stays under a percentage of the ref (e.g. 0.85 = 85%)" },
  { value: "min_ratio",  label: "value ≥ ref × ratio — value stays above a percentage of the ref" },
];

function ParameterEditor({ eqId, params, equipmentName, onChanged, onHistory }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(newDraft());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function newDraft() {
    return {
      name: "", unit: "",
      target_kind: "", target_value: "", target_min: "", target_max: "",
      warn_pct: 5, alarm_pct: 10,
      mandatory: false, photo_required: false, notes_allowed: true,
      dependencies: [],
      // Iter 387 — calculated parameter fields
      kind: "measured",
      op: "sum",
      operands: [],              // [{parameter_id, source, coefficient}]
      denominator_operands: [],  // ratio only
      as_percent: false,
    };
  }

  const buildTarget = (d) => {
    if (!d.target_kind) return {};
    const t = { kind: d.target_kind, warn_pct: Number(d.warn_pct) || 5, alarm_pct: Number(d.alarm_pct) || 10 };
    if (d.target_kind === "exact") t.value = Number(d.target_value);
    if (d.target_kind === "min") t.min = Number(d.target_min);
    if (d.target_kind === "max") t.max = Number(d.target_max);
    if (d.target_kind === "range") { t.min = Number(d.target_min); t.max = Number(d.target_max); }
    if (d.target_kind === "yes_no") return { kind: "yes_no" };
    return t;
  };

  const submit = async () => {
    if (!draft.name.trim()) { setErr("Name is required."); return; }
    const hasDup = (ops) => {
      const seen = new Set();
      return (ops || []).some((o) => {
        if (!o.parameter_id) return false;
        const k = `${o.parameter_id}|${o.source || "current"}`;
        if (seen.has(k)) return true;
        seen.add(k);
        return false;
      });
    };
    if (draft.kind === "calculated" && (hasDup(draft.operands) || hasDup(draft.denominator_operands))) {
      setErr("The same parameter is used twice in one part of the formula. Fix the highlighted row before saving.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        equipment_id: eqId,
        name: draft.name.trim(),
        unit: draft.unit || null,
        target: buildTarget(draft),
        mandatory: !!draft.mandatory,
        photo_required: !!draft.photo_required,
        notes_allowed: draft.notes_allowed !== false,
        dependencies: (draft.dependencies || [])
          .filter((d) => d.ref_param_id && d.op)
          .map((d) => ({
            ref_param_id: d.ref_param_id,
            op: d.op,
            // Iter 375 — value stored as string in the FE so negatives
            // and free typing work; cast on submit. Empty / non-numeric
            // → 0 (safe default).
            value: (() => {
              const n = Number(d.value);
              return Number.isFinite(n) ? n : 0;
            })(),
            message: d.message || null,
          })),
        display_order: editingId ? undefined : params.length,
        // Iter 387 — calculated parameter payload
        kind: draft.kind || "measured",
        formula: draft.kind === "calculated" ? {
          op: draft.op || "sum",
          operands: (draft.operands || []).filter((o) => o.parameter_id).map((o) => ({
            parameter_id: o.parameter_id,
            source: o.source || "current",
            coefficient: Number(o.coefficient) || 1,
          })),
          denominator_operands: draft.op === "ratio"
            ? (draft.denominator_operands || []).filter((o) => o.parameter_id).map((o) => ({
                parameter_id: o.parameter_id,
                source: o.source || "current",
                coefficient: Number(o.coefficient) || 1,
              }))
            : [],
          as_percent: draft.op === "ratio" ? !!draft.as_percent : false,
        } : null,
      };
      if (editingId) {
        await api.patch(`/perf/parameters/${editingId}`, body);
        toast.success("Parameter updated");
      } else {
        await api.post("/perf/parameters", body);
        toast.success("Parameter added");
      }
      setEditingId(null);
      setDraft(newDraft());
      onChanged?.();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const startEdit = (p) => {
    const t = p.target || {};
    const f = p.formula || {};
    setEditingId(p.id);
    setDraft({
      name: p.name || "",
      unit: p.unit || "",
      target_kind: t.kind || "",
      target_value: t.value != null ? String(t.value) : "",
      target_min: t.min != null ? String(t.min) : "",
      target_max: t.max != null ? String(t.max) : "",
      warn_pct: t.warn_pct ?? 5,
      alarm_pct: t.alarm_pct ?? 10,
      mandatory: !!p.mandatory,
      photo_required: !!p.photo_required,
      notes_allowed: p.notes_allowed !== false,
      // Iter 379 — normalise dep values to STRING so the text input
      // renders whatever the user types (previously 0 got stuck in
      // when the DB stored a number and the ?? fallback rendered "0"
      // instead of an empty editable field).
      dependencies: (Array.isArray(p.dependencies) ? p.dependencies : []).map((d) => ({
        ...d,
        value: d.value == null || d.value === "" ? "" : String(d.value),
      })),
      kind: p.kind || "measured",
      op: f.op || "sum",
      operands: f.operands || [],
      denominator_operands: f.denominator_operands || [],
      as_percent: !!f.as_percent,
    });
    setErr("");
  };

  const removeParam = async (pid) => {
    if (!window.confirm("Delete this parameter permanently? Historical readings remain but new captures will drop it.")) return;
    try {
      await api.delete(`/perf/parameters/${pid}`);
      onChanged?.();
      toast.success("Parameter deleted");
    } catch (e) { setErr(formatErr(e)); }
  };

  const cleanupDuplicates = async () => {
    if (!window.confirm("Remove duplicate/soft-deleted parameters on this equipment? Keeps the newest of each name.")) return;
    try {
      const { data } = await api.post(`/perf/parameters/cleanup?equipment_id=${eqId}`);
      toast.success(`Cleaned: ${data.duplicates_removed} duplicate(s), ${data.soft_deleted_removed} orphan(s) removed`);
      onChanged?.();
    } catch (e) { setErr(formatErr(e)); }
  };

  const otherParams = useMemo(
    () => params.filter((p) => p.id !== editingId),
    [params, editingId],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
          Parameters · <span className="text-white/70">{equipmentName}</span>
        </div>
        <button
          type="button"
          onClick={cleanupDuplicates}
          className="text-[10px] text-[#f59e0b] hover:text-white flex items-center gap-1"
          title="Remove duplicate & soft-deleted parameters"
          data-testid="perf-param-cleanup"
        >
          <Trash2 className="w-3 h-3" /> Clean up duplicates
        </button>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {params.map((p) => (
          <div key={p.id} className="hv-card p-3" data-testid={`perf-param-cfg-${p.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {p.name} {p.unit && <span className="text-[10px] text-[#647087]">({p.unit})</span>}
                </div>
                <div className="text-[10px] text-[#647087] mt-0.5">
                  {p.target?.kind
                    ? `${p.target.kind}${p.target.value != null ? ` ${p.target.value}` : ""}${p.target.min != null ? ` ${p.target.min}` : ""}${p.target.max != null ? `–${p.target.max}` : ""}`
                    : "no target"}
                  {p.mandatory && " · mandatory"}
                  {p.photo_required && " · photo required"}
                  {(p.dependencies || []).length > 0 && (
                    <span className="text-[#a78bfa]"> · {p.dependencies.length} dep{p.dependencies.length === 1 ? "" : "s"}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => onHistory?.(p)}
                  className="p-1.5 rounded-md hover:bg-white/5 text-[#647087]"
                  title="Audit history"
                  data-testid={`perf-param-history-${p.id}`}
                >
                  <History className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => startEdit(p)}
                  className="p-1.5 rounded-md hover:bg-white/5 text-[#a0aabf]"
                  title="Edit parameter"
                  data-testid={`perf-param-edit-${p.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeParam(p.id)}
                  className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-300"
                  data-testid={`perf-param-del-${p.id}`}
                  title="Remove parameter"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {!params.length && <div className="text-xs text-[#647087] py-3">No parameters yet — add one below.</div>}
      </div>

      <div className="hv-card p-3 space-y-2" data-testid="perf-param-form">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
            {editingId ? "Edit parameter" : "Add parameter"}
          </div>
          {editingId && (
            <button
              type="button"
              onClick={() => { setEditingId(null); setDraft(newDraft()); }}
              className="text-[10px] text-[#647087] hover:text-white flex items-center gap-1"
              data-testid="perf-param-edit-cancel"
            >
              <X className="w-3 h-3" /> Cancel edit
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Name (e.g. Feed pressure)"
            className="hv-input text-sm"
            data-testid="perf-param-new-name"
          />
          <input
            value={draft.unit}
            onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
            placeholder="Unit (e.g. bar)"
            className="hv-input text-sm"
            data-testid="perf-param-new-unit"
          />
        </div>
        <div className="text-[10px] text-[#647087] -mt-1">
          The name and unit shown to techs when capturing this reading.
        </div>

        {/* Iter 387 — Kind toggle: measured vs calculated */}
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#647087] pt-2">
          Type
        </div>
        <div className="flex items-center gap-1">
          {[
            { v: "measured",   label: "Measured",   hint: "Tech types a value" },
            { v: "calculated", label: "Calculated", hint: "Auto-derived" },
          ].map((k) => (
            <button
              key={k.v}
              type="button"
              onClick={() => setDraft({ ...draft, kind: k.v })}
              className={`text-[11px] px-2 py-1 rounded border transition ${draft.kind === k.v ? "border-[#1FB6C1] bg-[#1FB6C1]/15 text-[#1FB6C1]" : "border-white/10 text-[#a0aabf] hover:border-white/20"}`}
              data-testid={`perf-param-kind-${k.v}`}
              title={k.hint}
            >
              {k.label}
            </button>
          ))}
        </div>

        {/* Iter 387 — Formula editor (only for calculated params) */}
        {draft.kind === "calculated" && (
          <div className="space-y-2 border-l-2 border-[#A78BFA]/40 pl-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#647087]">
              Formula
            </div>
            <select
              value={draft.op}
              onChange={(e) => setDraft({ ...draft, op: e.target.value })}
              className="hv-input text-sm"
              data-testid="perf-param-formula-op"
            >
              <option value="sum">Sum (add / subtract with signs)</option>
              <option value="delta">Delta (this-day − previous-day of a meter)</option>
              <option value="ratio">Ratio (numerator ÷ denominator)</option>
            </select>

            {draft.op === "sum" && (
              <FormulaOperandsEditor
                params={params}
                editingId={editingId}
                operands={draft.operands}
                onChange={(ops) => setDraft({ ...draft, operands: ops })}
                allowCoefficient
                label="Terms — tap the +/− pill to add or subtract each parameter"
              />
            )}
            {draft.op === "delta" && (
              <FormulaOperandsEditor
                params={params}
                editingId={editingId}
                operands={draft.operands}
                onChange={(ops) => setDraft({ ...draft, operands: ops.slice(0, 1) })}
                singleOperand
                label="Cumulative meter (this-day − most-recent-prior)"
              />
            )}
            {draft.op === "ratio" && (
              <>
                <div className="text-[10px] text-[#647087] leading-relaxed border border-white/5 rounded-md p-2 bg-black/20">
                  <b className="text-[#a78bfa]">Example only, not your setup:</b> RO Recovery with recycling.<br/>
                  Numerator: <span className="text-emerald-300">+ Permeate</span><br/>
                  Denominator: <span className="text-emerald-300">+ Permeate</span> <span className="text-emerald-300">+ Concentrate</span> <span className="text-rose-300">− Recycling</span>
                </div>
                <FormulaOperandsEditor
                  params={params}
                  editingId={editingId}
                  operands={draft.operands}
                  onChange={(ops) => setDraft({ ...draft, operands: ops })}
                  allowCoefficient
                  label="Numerator — top of the fraction"
                />
                <div className="text-center text-[#647087] font-mono text-lg leading-none">─── ÷ ───</div>
                <FormulaOperandsEditor
                  params={params}
                  editingId={editingId}
                  operands={draft.denominator_operands}
                  onChange={(ops) => setDraft({ ...draft, denominator_operands: ops })}
                  allowCoefficient
                  label="Denominator — bottom of the fraction"
                />
                <label className="flex items-center gap-1.5 text-[11px] text-[#a0aabf]">
                  <input
                    type="checkbox"
                    checked={!!draft.as_percent}
                    onChange={(e) => setDraft({ ...draft, as_percent: e.target.checked })}
                    data-testid="perf-param-formula-as-percent"
                  />
                  Show as percentage (× 100)
                </label>
              </>
            )}
            {/* Iter 390 — Live formula preview */}
            <FormulaPreview draft={draft} params={params} />
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.15em] text-[#647087] pt-2">
          Target — how the app decides if a reading is OK, warn or alarm
        </div>
        <div className="grid grid-cols-3 gap-2">
          <select
            value={draft.target_kind}
            onChange={(e) => setDraft({ ...draft, target_kind: e.target.value })}
            className="hv-input text-sm"
            data-testid="perf-param-new-kind"
          >
            {TARGET_KINDS.map((k) => (<option key={k.value} value={k.value}>{k.label}</option>))}
          </select>
          {draft.target_kind === "exact" && (
            <input type="text" inputMode="decimal" value={draft.target_value} onChange={(e) => setDraft({ ...draft, target_value: e.target.value })} placeholder="Value" className="hv-input text-sm col-span-2" />
          )}
          {draft.target_kind === "min" && (
            <input type="text" inputMode="decimal" value={draft.target_min} onChange={(e) => setDraft({ ...draft, target_min: e.target.value })} placeholder="Min" className="hv-input text-sm col-span-2" />
          )}
          {draft.target_kind === "max" && (
            <input type="text" inputMode="decimal" value={draft.target_max} onChange={(e) => setDraft({ ...draft, target_max: e.target.value })} placeholder="Max" className="hv-input text-sm col-span-2" />
          )}
          {draft.target_kind === "range" && (
            <>
              <input type="text" inputMode="decimal" value={draft.target_min} onChange={(e) => setDraft({ ...draft, target_min: e.target.value })} placeholder="Min" className="hv-input text-sm" />
              <input type="text" inputMode="decimal" value={draft.target_max} onChange={(e) => setDraft({ ...draft, target_max: e.target.value })} placeholder="Max" className="hv-input text-sm" />
            </>
          )}
        </div>
        {draft.target_kind && draft.target_kind !== "yes_no" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-[#a0aabf]">Warn %
                <input type="text" inputMode="decimal" value={draft.warn_pct} onChange={(e) => setDraft({ ...draft, warn_pct: e.target.value })} className="hv-input text-sm mt-1" />
              </label>
              <label className="text-xs text-[#a0aabf]">Alarm %
                <input type="text" inputMode="decimal" value={draft.alarm_pct} onChange={(e) => setDraft({ ...draft, alarm_pct: e.target.value })} className="hv-input text-sm mt-1" />
              </label>
            </div>
            <div className="text-[10px] text-[#647087] -mt-1">
              How far the value can drift from the target before the reading changes colour.
              Below <b>Warn %</b> → green (OK). Between Warn and Alarm → amber (warn). Beyond <b>Alarm %</b> → red (alarm).
            </div>
          </>
        )}
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#647087] pt-2">
          Capture rules — what the tech must do when logging this parameter
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-[#a0aabf]">
          <label className="flex items-center gap-1" title="Reading cannot be left blank when saving the daily log.">
            <input type="checkbox" checked={draft.mandatory} onChange={(e) => setDraft({ ...draft, mandatory: e.target.checked })} /> Mandatory
          </label>
          <label className="flex items-center gap-1" title="Tech must attach at least one photo before this reading can be saved.">
            <input type="checkbox" checked={draft.photo_required} onChange={(e) => setDraft({ ...draft, photo_required: e.target.checked })} /> Photo required
          </label>
          <label className="flex items-center gap-1" title="Show the notes field (and voice dictation button) for this parameter.">
            <input type="checkbox" checked={draft.notes_allowed} onChange={(e) => setDraft({ ...draft, notes_allowed: e.target.checked })} /> Notes allowed
          </label>
        </div>
        <div className="text-[10px] text-[#647087] -mt-1">
          Hover a checkbox for a full description. Mandatory blocks the daily save if empty;
          photo-required forces a picture; notes-allowed reveals the notes + mic on capture.
        </div>

        <DependencyEditor
          value={draft.dependencies}
          onChange={(deps) => setDraft({ ...draft, dependencies: deps })}
          otherParams={otherParams}
        />

        {err && <div className="text-sm text-rose-300">{err}</div>}
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={submit}
            disabled={busy || !draft.name.trim()}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="perf-param-add-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (editingId ? <Save className="w-3 h-3" /> : <Plus className="w-3 h-3" />)}
            {editingId ? "Save changes" : "Add parameter"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * DependencyEditor — Iter 374. Repeatable list of {ref_param_id, op, value, message}.
 */
function DependencyEditor({ value, onChange, otherParams }) {
  const list = Array.isArray(value) ? value : [];
  const add = () => onChange([...list, { ref_param_id: "", op: "lte", value: "", message: "" }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const patch = (i, k, v) => onChange(list.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  return (
    <div className="pt-2 border-t border-white/5" data-testid="perf-param-deps">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a78bfa] flex items-center gap-1">
          <LinkIcon className="w-3 h-3" /> Dependencies (live cross-parameter rules)
        </div>
        <button
          type="button"
          onClick={add}
          disabled={otherParams.length === 0}
          className="text-[10px] text-[#a78bfa] hover:text-white disabled:opacity-40 flex items-center gap-1"
          data-testid="perf-param-dep-add"
        >
          <Plus className="w-3 h-3" /> Add rule
        </button>
      </div>
      <div className="text-[10px] text-[#647087] mt-0.5 leading-relaxed">
        Compare this reading to another parameter on the same equipment. Break the rule → the reading turns red (alarm), even if the target range is fine.
        <br />Example: <em>Outlet pressure — max downward variance from Inlet pressure = 2 bar</em> means outlet may not drop more than 2 bar below inlet.
      </div>
      {otherParams.length === 0 && list.length === 0 && (
        <div className="text-[10px] text-[#647087] mt-1">Add at least one other parameter first to reference it.</div>
      )}
      <div className="space-y-2 mt-2">
        {list.map((d, i) => (
          <div key={i} className="hv-card p-2 space-y-1.5 border-[#a78bfa]/30" data-testid={`perf-param-dep-row-${i}`}>
            <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto] gap-1 items-center">
              <select
                value={d.ref_param_id}
                onChange={(e) => patch(i, "ref_param_id", e.target.value)}
                className="hv-input text-xs"
                data-testid={`perf-param-dep-ref-${i}`}
              >
                <option value="">Reference parameter…</option>
                {otherParams.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.unit ? ` (${p.unit})` : ""}</option>
                ))}
              </select>
              <select
                value={d.op}
                onChange={(e) => patch(i, "op", e.target.value)}
                className="hv-input text-xs"
                data-testid={`perf-param-dep-op-${i}`}
              >
                {DEP_OPS.map((op) => (<option key={op.value} value={op.value}>{op.label}</option>))}
              </select>
              <input
                type="text"
                inputMode="decimal"
                value={d.value ?? ""}
                onChange={(e) => patch(i, "value", e.target.value)}
                onFocus={(e) => e.target.select()}
                placeholder="value"
                className="hv-input text-xs"
                data-testid={`perf-param-dep-val-${i}`}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                className="p-1 rounded hover:bg-rose-500/10 text-rose-300"
                title="Remove rule"
                data-testid={`perf-param-dep-del-${i}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <input
              value={d.message || ""}
              onChange={(e) => patch(i, "message", e.target.value)}
              placeholder="Optional warning message (e.g. 'Δ pressure too large')"
              className="hv-input w-full text-xs"
              data-testid={`perf-param-dep-msg-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditHistoryModal({ entity, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/perf/audit-log?entity_type=${entity.entity_type}&entity_id=${entity.entity_id}&limit=100`)
      .then(({ data }) => setRows(data))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [entity]);

  return (
    <Modal open onClose={onClose} title={`Audit log · ${entity.name}`} maxWidth="max-w-lg">
      <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="perf-audit-modal">
        {loading && <div className="text-xs text-[#647087]">Loading…</div>}
        {err && <div className="text-sm text-rose-300">{err}</div>}
        {!loading && rows.length === 0 && (
          <div className="text-xs text-[#647087] py-4 text-center">No edits yet.</div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="hv-card p-3" data-testid={`perf-audit-row-${r.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#a0aabf] flex justify-between">
              <span>{r.by_name}</span>
              <span>{(r.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <ul className="mt-1 space-y-0.5 text-xs">
              {Object.entries(r.diff || {}).map(([field, chg]) => (
                <li key={field} className="text-[#a0aabf]">
                  <b className="text-white">{field}</b>:{" "}
                  <span className="line-through text-[#647087]">{JSON.stringify(chg.before)}</span>{" → "}
                  <span className="text-emerald-300">{JSON.stringify(chg.after)}</span>
                </li>
              ))}
            </ul>
            {r.reason && <div className="mt-1 text-[10px] text-[#647087]">Reason: {r.reason}</div>}
          </div>
        ))}
      </div>
    </Modal>
  );
}

function FormulaOperandsEditor({ params, editingId, operands, onChange, allowCoefficient, singleOperand, label, showLeadingSign = false }) {
  // Iter 387 → Iter 390 — multi-row picker with param dropdown +
  // signed coefficient (sign is a big +/− toggle, multiplier is a small
  // separate field defaulting to 1). Much clearer than a raw number
  // input that quietly accepts negatives.
  const options = (params || []).filter((p) => p.id !== editingId);

  const addRow = () => {
    if (singleOperand && (operands || []).length >= 1) return;
    onChange([...(operands || []), { parameter_id: "", source: "current", coefficient: 1 }]);
  };
  const updateRow = (idx, patch) => {
    onChange((operands || []).map((o, i) => i === idx ? { ...o, ...patch } : o));
  };
  const removeRow = (idx) => onChange((operands || []).filter((_, i) => i !== idx));
  const toggleSign = (idx, current) => {
    const c = Number(current) || 1;
    updateRow(idx, { coefficient: c * -1 });
  };
  const setMagnitude = (idx, current, magnitude) => {
    const c = Number(current) || 1;
    const sign = c < 0 ? -1 : 1;
    const mag = Math.abs(Number(magnitude) || 1);
    updateRow(idx, { coefficient: sign * mag });
  };

  return (
    <div className="space-y-1.5">
      <div className="text-[10px] text-[#647087]">{label}</div>
      {(operands || []).map((o, idx) => {
        const coef = Number(o.coefficient) || 1;
        const isNeg = coef < 0;
        const mag = Math.abs(coef);
        const dup = !!o.parameter_id && (operands || []).some((x, j) => j < idx && x.parameter_id === o.parameter_id && (x.source || "current") === (o.source || "current"));
        return (
          <div key={idx} className={`flex items-center gap-1.5 flex-wrap ${dup ? "ring-1 ring-rose-500/60 rounded-md p-1" : ""}`} data-testid={`perf-formula-operand-${idx}`}>
            {dup && <div className="w-full text-[10px] text-rose-300" data-testid={`perf-formula-operand-${idx}-dup`}>This parameter is already used above. Pick the right one, e.g. Permeate or Concentrate.</div>}
            {allowCoefficient && (
              <>
                {/* Sign toggle — massive +/− button */}
                <button
                  type="button"
                  onClick={() => toggleSign(idx, coef)}
                  className={`px-2 py-1 rounded-md text-sm font-bold w-8 shrink-0 transition ${isNeg ? "bg-rose-500/25 text-rose-200 border border-rose-500/40" : "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"}`}
                  title={isNeg ? "Subtract this term — click to flip to +" : "Add this term — click to flip to −"}
                  data-testid={`perf-formula-operand-${idx}-sign`}
                >
                  {isNeg ? "−" : "+"}
                </button>
                {/* Optional non-1 multiplier (usually left at 1). */}
                {(showLeadingSign || mag !== 1) && (
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={mag}
                    onChange={(e) => setMagnitude(idx, coef, e.target.value)}
                    className="hv-input text-xs w-[54px]"
                    title="Multiplier (usually 1)"
                    data-testid={`perf-formula-operand-${idx}-mag`}
                  />
                )}
              </>
            )}
            <select
              value={o.parameter_id || ""}
              onChange={(e) => updateRow(idx, { parameter_id: e.target.value })}
              className="hv-input text-xs flex-1 min-w-[160px]"
              data-testid={`perf-formula-operand-${idx}-param`}
            >
              <option value="">— pick a parameter —</option>
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.unit ? ` (${p.unit})` : ""}{p.kind === "calculated" ? " [calc]" : ""}
                </option>
              ))}
            </select>
            <select
              value={o.source || "current"}
              onChange={(e) => updateRow(idx, { source: e.target.value })}
              className="hv-input text-xs"
              data-testid={`perf-formula-operand-${idx}-source`}
            >
              <option value="current">today</option>
              <option value="previous">previous day</option>
            </select>
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="text-[#647087] hover:text-rose-300 p-0.5"
              title="Remove"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}
      {(!singleOperand || (operands || []).length === 0) && (
        <button
          type="button"
          onClick={addRow}
          className="hv-btn hv-btn-ghost text-[10px] py-0.5"
          data-testid={`perf-formula-add-${label?.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        >
          <Plus className="w-3 h-3" /> Add {singleOperand ? "meter" : "term"}
        </button>
      )}
    </div>
  );
}



/**
 * Iter 390 — FormulaPreview
 * Renders a human-readable, live-updating one-liner of what the formula
 * will compute. Helps techs confirm the sign of each term visually
 * before saving.
 *   sum:    Result = + A + B − C
 *   delta:  Result = A(today) − A(prev)
 *   ratio:  Result = (+ A) ÷ (+ A + B − C)     [× 100 if as_percent]
 */
function FormulaPreview({ draft, params }) {
  const paramName = (pid) => {
    const p = (params || []).find((x) => x.id === pid);
    if (!p) return "?";
    return p.name + (p.unit ? ` (${p.unit})` : "");
  };
  const renderOperand = (o, first = false, idx = 0) => {
    const c = Number(o.coefficient) || 1;
    const isNeg = c < 0;
    const mag = Math.abs(c);
    const signPart = isNeg ? "−" : (first ? "" : "+");
    const mult = mag === 1 ? "" : `${mag} × `;
    const nm = o.parameter_id ? paramName(o.parameter_id) : "?";
    const suf = o.source === "previous" ? " (prev)" : "";
    return (
      <span key={`${idx}_${o.parameter_id}_${c}_${o.source}`} className={isNeg ? "text-rose-300" : "text-emerald-300"}>
        {signPart} {mult}{nm}{suf}{" "}
      </span>
    );
  };
  const opName = draft?.op;
  let body = null;
  if (opName === "sum") {
    const ops = draft.operands || [];
    if (!ops.length) body = <span className="text-[#647087]">— add at least one term above —</span>;
    else body = <>{ops.map((o, i) => renderOperand(o, i === 0, i))}</>;
  } else if (opName === "delta") {
    const first = (draft.operands || [])[0];
    if (!first?.parameter_id) body = <span className="text-[#647087]">— pick a cumulative meter above —</span>;
    else body = (
      <>
        <span className="text-emerald-300">{paramName(first.parameter_id)} (today)</span>
        <span className="text-[#a0aabf]">{" "}−{" "}</span>
        <span className="text-emerald-300">{paramName(first.parameter_id)} (previous)</span>
      </>
    );
  } else if (opName === "ratio") {
    const numOps = draft.operands || [];
    const denOps = draft.denominator_operands || [];
    if (!numOps.length || !denOps.length) {
      body = <span className="text-[#647087]">— fill numerator AND denominator to preview —</span>;
    } else {
      body = (
        <span className="inline-flex items-center gap-1 flex-wrap">
          <span>(</span>
          {numOps.map((o, i) => renderOperand(o, i === 0, i))}
          <span>) ÷ (</span>
          {denOps.map((o, i) => renderOperand(o, i === 0, 100 + i))}
          <span>)</span>
          {draft.as_percent && <span className="text-[#a78bfa]">{" "}× 100</span>}
        </span>
      );
    }
  }
  return (
    <div className="mt-1 p-2 rounded-md bg-[#0a1628] border border-[#a78bfa]/30 text-[11px] font-mono leading-relaxed" data-testid="perf-formula-preview">
      <span className="text-[9px] uppercase tracking-wider text-[#a78bfa]">Preview · </span>
      <span className="text-white">{draft?.name || "This param"}</span>
      <span className="text-[#a0aabf]"> = </span>
      {body}
    </div>
  );
}
